<template>
  <div
    class="group ease-spatial relative flex h-8 items-center justify-between overflow-hidden rounded-full transition-all duration-400"
    :class="[
      announcement.readMore && 'cursor-pointer',
      announcement.type == AnnouncementType.News &&
        'bg-primary-container text-on-primary-container',
      announcement.type == AnnouncementType.Maintenance &&
        'bg-tertiary-container text-on-tertiary-container',
    ]"
    :style="{ width: !initialAnimated ? `${parentWidth}px` : 'auto' }"
    @click="readMore"
  >
    <div
      ref="container"
      class="ease-spatial flex h-full flex-row items-center p-0.5 whitespace-nowrap transition-transform duration-400"
      :class="[!animated && !initialAnimated && '-translate-x-1.5']"
    >
      <div class="ml-2 flex items-center gap-3">
        <BaseIcon size="22" class="">{{ announcementTypeBanner.icon }}</BaseIcon>
        <h2 class="text-title-small">
          {{ announcement.title }}
        </h2>
        <p
          v-if="!compact"
          class="text-body-medium transition-width ease-fast-spatial line-clamp-1 hidden h-full overflow-hidden lg:inline"
        >
          {{ announcement.content }}
        </p>
      </div>

      <BaseIcon v-if="announcement.readMore" size="20" class="ml-1">arrow_outward</BaseIcon>
      <button
        class="mr-1.5 ml-1.5 flex h-6 w-6 items-center justify-center rounded-full"
        v-tooltip="$t('announcements.dismiss')"
        @click.stop="emits('hideAnnouncement', announcement.id)"
      >
        <BaseIcon size="20" :filled="true" class="">close</BaseIcon>
      </button>
      <BaseStateLayer :type="announcement.type == AnnouncementType.News ? 'primary' : 'tertiary'" />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed, useTemplateRef, ref, onMounted } from 'vue'
  import { AnnouncementType, type Announcement } from './announcement'
  import BaseIcon from '../base/icon/BaseIcon.vue'
  import BaseStateLayer from '../base/state-layer/BaseStateLayer.vue'
  import { useElementSize } from '@vueuse/core'

  interface Props {
    announcement: Announcement & { animated: boolean }
    compact: boolean
  }

  const { announcement } = defineProps<Props>()
  const initialAnimated = ref(announcement.animated)
  onMounted(() => {
    setTimeout(() => {
      animated.value = true
      emits('animated', announcement.id)
    }, 1000)
  })

  const emits = defineEmits<{
    (e: 'hideAnnouncement', id: string): void
    (e: 'animated', id: string): void
  }>()

  const announcementTypeBanner = computed(() => {
    if (announcement.type === AnnouncementType.News) {
      return {
        colorClass: 'gorgonzola-container',
        icon: 'celebration',
      }
    }

    return {
      colorClass: 'dromedar-container',
      icon: 'warning_amber',
    }
  })

  const readMore = () => {
    if (announcement.readMore) {
      window.open(announcement.readMore, '_blank')
    }
  }

  const container = useTemplateRef<HTMLDivElement>('container')

  const { width } = useElementSize(container)
  const animated = ref(false)

  const parentWidth = computed(() => {
    if (animated.value) {
      return width.value
    }
    return 32
  })
</script>
